import {Delete} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {IconButton} from '@mui/material';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {employeeApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {ConformationModal} from 'src/components/common/ConformationModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {EmployeeDeleteRequest, EmployeeDTO} from 'src/types/employee';
import {NotifyError} from 'src/utils/notification';
import {employeeActionsSx} from '../EmployeeActions/EmployeeActions.sx';

type DeleteEmployeeMutationTrigger = ReturnType<
    typeof employeeApi.useDeleteEmployeeByIdMutation
>[0];

const handleDeleteEmployee =
    (
        trigger: DeleteEmployeeMutationTrigger,
        input: EmployeeDeleteRequest,
        dispatch: AppDispatch,
        handleClose: () => void,
        errorMessage: string
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));

                await trigger(input)
                    .unwrap()
                    .finally(() => {
                        dispatch(preloaderChangeStatus(false));
                    });
            } catch (error: any) {
                error.status === 400 && NotifyError(errorMessage);
            } finally {
                dispatch(preloaderChangeStatus(false));
                handleClose();
            }
        };

export const DeleteEmployee: FC<{ row: EmployeeDTO }> = ({row}) => {
    // The control is the action: without the permission the server refuses it, so showing it
    // offers a button whose only outcome is an error.
    const canDoIt = useCan('employees.delete');
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const dispatch = useAppDispatch();

    const [triggerDeleteEmployee] = employeeApi.useDeleteEmployeeByIdMutation();

    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'deleteEmployee'});

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    if (!canDoIt) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={employeeActionsSx.button}
                onClick={handleOpenModal}
            >
                <Delete sx={employeeActionsSx.icon}/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ConformationModal
                    handleClose={handleCloseModal}
                    handleSubmit={handleDeleteEmployee(
                        triggerDeleteEmployee,
                        {
                            employeeId: +row?.id,
                            params: {
                                businessId,
                                companyId: +row?.company?.id,
                            },
                        },
                        dispatch,
                        handleCloseModal,
                        intl.formatMessage({id: 'cannotDeleteEmployee'})
                    )}
                    text={'deleteEmployeeConfirmationQuestion'}
                    caption={`${row.firstName} ${row.lastName}`}
                />
            </AMPModal>
        </>
    );
};
